import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { AppCoordinator } from '../../app/AppCoordinator';

const WINDOWS_ROOT_FALLBACK = 'C:\\Windows';
const PROGRAM_FILES_FALLBACK = 'C:\\Program Files';

function environmentValue(name: string): string | undefined {
  if (typeof process === 'undefined' || !process.env) return undefined;
  const value = process.env[name];
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

function joinWindows(...parts: string[]): string {
  return parts
    .map((part, index) => (index === 0 ? part.replace(/[\\/]+$/, '') : part.replace(/^[\\/]+|[\\/]+$/g, '')))
    .filter((part) => part.length > 0)
    .join('\\');
}

function windowsRoot(): string {
  const root = environmentValue('SystemRoot') ?? environmentValue('windir') ?? WINDOWS_ROOT_FALLBACK;
  return /^[A-Za-z]:\\/.test(root) ? root.replace(/[\\/]+$/, '') : WINDOWS_ROOT_FALLBACK;
}

function programFilesDirectory(): string {
  const directory = environmentValue('ProgramFiles') ?? PROGRAM_FILES_FALLBACK;
  return /^[A-Za-z]:\\/.test(directory) ? directory.replace(/[\\/]+$/, '') : PROGRAM_FILES_FALLBACK;
}

function systemDrive(): string {
  const drive = windowsRoot().match(/^[A-Za-z]:/);
  return drive ? `${drive[0]}\\` : 'C:\\';
}

function normalizeWindowsPath(filePath: string): string {
  return filePath.replace(/\//g, '\\').replace(/\\+$/, '').toLowerCase();
}

/** Installed launcher, resolved from ProgramFiles rather than a fixed drive. */
export function playcastLauncherPath(): string {
  return joinWindows(programFilesDirectory(), 'Playcast', 'PlaycastLauncher.exe');
}

function allowedExecutables(): { powershell: string; cmd: string } {
  const root = windowsRoot();
  return {
    powershell: joinWindows(root, 'System32', 'WindowsPowerShell', 'v1.0', 'powershell.exe'),
    cmd: joinWindows(root, 'System32', 'cmd.exe'),
  };
}

function resolveAllowedExecutable(requested: string): string | null {
  const allowed = allowedExecutables();
  const alias = requested.trim().toLowerCase();
  if (alias === 'powershell') return allowed.powershell;
  if (alias === 'cmd') return allowed.cmd;
  const normalized = normalizeWindowsPath(requested);
  if (normalized === normalizeWindowsPath(allowed.powershell)) return allowed.powershell;
  if (normalized === normalizeWindowsPath(allowed.cmd)) return allowed.cmd;
  return null;
}

/** Preserves the admin control boundary's exact executable allowlist and IPC provenance. */
export class AdminElevatedProcessManager {
  public constructor(private readonly ipc: Pick<IIpcService, 'send'>,
    private readonly app: Pick<AppCoordinator, 'getHostAuthId'>) {}

  public launch(message: Record<string, unknown>): boolean {
    const requested = typeof message.path === 'string' ? message.path : '';
    const executable = resolveAllowedExecutable(requested);
    const args = typeof message.arguments === 'string' ? message.arguments : '';
    const requestedDirectory = typeof message.workingDirectory === 'string' ? message.workingDirectory : '';
    const cwd = requestedDirectory.length > 0 ? requestedDirectory : systemDrive();
    if (!executable) {
      console.error('[AdminPeerHandler] Rejected elevated launch — path not in allowlist:', requested);
      return false;
    }
    this.ipc.send(generators.system.launchElevatedProcess(this.app.getHostAuthId(), executable, args, cwd,
      undefined, { fromAdminSession: true }));
    console.log('[AdminPeerHandler] On-demand elevated process launch requested:', executable);
    return true;
  }

  /** Handles the fixed dashboard recovery command without exposing path input. */
  public restorePlaycast(): boolean {
    const launcher = playcastLauncherPath();
    const installDirectory = launcher.slice(0, launcher.lastIndexOf('\\'));
    this.ipc.send(generators.system.launchElevatedProcess(
      this.app.getHostAuthId(),
      launcher,
      '/run /pavilion',
      installDirectory,
      undefined,
      { fromAdminSession: true },
    ));
    console.log('[AdminPeerHandler] Playcast desktop recovery requested');
    return true;
  }
}
